import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Divider,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Public as PublicIcon } from "@mui/icons-material";
import Swal from 'sweetalert2';
import { EventTabContext } from "./EventTabContext";
import { useEventData } from "./EventDataContext";
import LogoImage from "../../statics/logo.svg";

export default function EventsContent({ steps, TabHandler, eventId }) {
  const [activeSection, setActiveSection] = useState("information");
  const [mainTab, setMainTab] = useState("settings");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { saveEventData, loadEventData, isDirty, eventData, deleteEvent } = useEventData();

  useEffect(() => {
    if (eventId) {
      loadEventData(eventId);
    }
  }, [eventId, loadEventData]);

  // Update browser title based on event name
  useEffect(() => {
    const eventName = eventData?.information?.eventName?.value;
    if (eventName) {
      document.title = `${eventName} - SmileBurst Admin`;
    } else {
      document.title = "SmileBurst Admin";
    }

    return () => {
      document.title = "SmileBurst Admin";
    };
  }, [eventData?.information?.eventName?.value]);

  const filteredSteps = steps.filter(step => {
    if (step.key === 'gift') {
      return eventData?.gift?.giftButton?.enabled?.value;
    }
    if (step.key === 'wish') {
      return eventData?.main?.wishButton?.enabled?.value;
    }
    return true;
  });

  const activitySteps = filteredSteps.filter(step => step.group === 'activity');
  const settingsSteps = filteredSteps.filter(step => step.group !== 'activity');

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
  };

  const BackToHome = () => {
    navigate("/");
  };

  const handleLogout = () => {
    navigate("/login");
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      const deleteResult = await deleteEvent(eventId);
      if (deleteResult.success) {
        await Swal.fire(
          'Deleted!',
          'Your event has been deleted.',
          'success'
        );
        navigate("/");
      } else {
        Swal.fire(
          'Error!',
          'Failed to delete event: ' + deleteResult.error,
          'error'
        );
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveEventData(eventId);
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: result.message || 'Saved successfully!',
          icon: 'success',
          confirmButtonColor: '#8A5918',
          timer: 3000,
          timerProgressBar: true
        }).then(() => {
          navigate(`/event/${result.eventId || eventId}`);
        });
      } else {
        let errorHtml = result.message || 'Failed to save';

        if (result.validationErrors && Object.keys(result.validationErrors).length > 0) {
          const errorList = [];

          // Helper to format field names (camelCase to Title Case)
          const formatFieldName = (str) => {
            return str
              .replace(/([A-Z])/g, ' $1') // insert space before caps
              .replace(/^./, (str) => str.toUpperCase()); // uppercase first char
          };

          // Helper to format section names
          const formatSectionName = (str) => {
            const map = {
              information: 'Event Details',
              theme: 'Theme',
              main: 'Main Page',
              gift: 'Gift Settings',
              wish: 'Wish Settings',
              album: 'Album Settings'
            };
            return map[str] || formatFieldName(str);
          };

          Object.entries(result.validationErrors).forEach(([section, fields]) => {
            Object.keys(fields).forEach(field => {
              // Handle nested errors if any (though currently mostly flat per section)
              if (typeof fields[field] === 'string') {
                errorList.push(`<li><b>${formatSectionName(section)}</b>: ${formatFieldName(field)}</li>`);
              } else if (typeof fields[field] === 'object') {
                Object.keys(fields[field]).forEach(subField => {
                  errorList.push(`<li><b>${formatSectionName(section)}</b>: ${formatFieldName(field)} - ${formatFieldName(subField)}</li>`);
                });
              }
            });
          });

          if (errorList.length > 0) {
            errorHtml = `
              <div style="text-align: left;">
                <p>Please fill in the following required fields:</p>
                <ul style="margin-top: 10px; padding-left: 20px; color: #E94C4C;">
                  ${errorList.join('')}
                </ul>
              </div>
            `;
          }
        }

        Swal.fire({
          title: 'Validation Error!',
          html: errorHtml,
          icon: 'error',
          confirmButtonColor: '#E94C4C'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#E94C4C'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStep = (step) => (
    <Box key={step.key} id={step.key}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: '5px', height: '32px', backgroundColor: '#8A5918', mr: 1.5, borderRadius: '2.5px' }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "#1A1A1A",
            textAlign: "left",
          }}
        >
          {step.label}
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: "16px",
          border: "1px solid #E0E0E0",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)",
        }}
      >
        {step.component}
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#F4F6F8", minHeight: "100vh" }}>
      {/* Navbar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "white",
          borderBottom: "1px solid #E0E0E0",
          width: "100%",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={BackToHome}
            sx={{
              color: "#8A5918",
              fontWeight: "600",
              textTransform: "none",
              mr: 2,
              "&:hover": { backgroundColor: "rgba(138, 89, 24, 0.04)" },
            }}
          >
            Back
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, cursor: "pointer" }} onClick={BackToHome}>
            <img
              src={LogoImage}
              alt="SmileBurst Logo"
              style={{ height: 40, marginRight: 10 }}
              onError={(e) => {
                e.target.src =
                  "https://placehold.co/120x40/EEEEEE/8A5918?text=SmileBurst";
                e.target.onerror = null;
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {isDirty && (
              <Typography
                variant="caption"
                sx={{ color: "#FFA500", fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
              >
                Unsaved changes
              </Typography>
            )}
            {eventId && (
              <Button
                variant="outlined"
                startIcon={<PublicIcon />}
                onClick={() => window.open(`/public/activity/${eventId}`, '_blank')}
                sx={{
                  fontWeight: "600",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  borderColor: "#8A5918",
                  color: "#8A5918",
                  "&:hover": {
                    borderColor: "#6b3b07",
                    backgroundColor: "rgba(138, 89, 24, 0.04)",
                  },
                }}
              >
                Public Activity
              </Button>
            )}
            {eventId && (
              <Button
                variant="outlined"
                color="error"
                sx={{
                  fontWeight: "600",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  borderColor: "#d32f2f",
                  color: "#d32f2f",
                  "&:hover": {
                    borderColor: "#c62828",
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                  },
                }}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
            <Button
              variant="contained"
              sx={{
                bgcolor: "#8A5918",
                color: "white",
                fontWeight: "600",
                borderRadius: "8px",
                px: 3,
                py: 1,
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { bgcolor: "#6b3b07", boxShadow: "none" },
              }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: "flex",
          minHeight: "calc(100vh - 65px)",
          position: "relative",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {/* Main Tabs */}
        <Box sx={{ bgcolor: "white", borderBottom: 1, borderColor: "divider", px: { xs: 2, md: 4 } }}>
          <Container maxWidth="lg">
            <Tabs
              value={mainTab}
              onChange={handleMainTabChange}
              aria-label="event tabs"
              sx={{
                '& .MuiTab-root': {
                  color: '#666',
                  '&.Mui-selected': {
                    color: '#8A5918',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#8A5918',
                }
              }}
            >
              <Tab label="Settings" value="settings" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '16px' }} />
              <Tab label="Activity" value="activity" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '16px' }} />
            </Tabs>
          </Container>
        </Box>

        {/* Main Content */}
        <EventTabContext.Provider value={{ tab: activeSection, setTab: setActiveSection }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 2, md: 4 },
              height: "calc(100vh - 113px)",
              overflow: "auto",
              width: "100%",
            }}
          >
            <Container maxWidth="lg" sx={{ pb: 5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

                {mainTab === "settings" && settingsSteps.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {settingsSteps.map(renderStep)}
                  </Box>
                )}

                {mainTab === "activity" && activitySteps.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {activitySteps.map(renderStep)}
                  </Box>
                )}

              </Box>
            </Container>
          </Box>
        </EventTabContext.Provider>
      </Box>
    </Box>
  );
}
